import React, { ReactNode, useContext, useEffect, useState } from 'react';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';

import { ApplicationRoute } from 'routes/routes.enums';
import Spinner from 'components/common/Spinner/Spinner';
import { RootStoreContext } from 'index';
import { hasPermission } from 'components/permissions/permissions.utils';
import { PermissionName } from 'components/permissions/permissions.enum';
import { AccountStatus } from 'components/users/users.enums';
import ConsentDialog from 'components/MainLayout/ConsentDialog/ConsentDialog';

import { MDA_WAS_SIGNED_DAYS_PASSED } from './routes.constants';

interface PrivateRouteProps {
  requiredPermissions?: string[];
  children?: ReactNode;
}

const PrivateRoute = ({ requiredPermissions = [] }: PrivateRouteProps) => {
  const { authStore, intermediaryApplicationsStore, permissionsStore, applicationsStore, globalModalsStore } =
    useContext(RootStoreContext);
  const { getCompanyProfile, getMdaContacts, companyProfile } = intermediaryApplicationsStore;
  const { getAccount, account, setAuthenticated } = authStore;
  const { openFeatureRestricted } = globalModalsStore;
  const navigator = useNavigate();
  const refreshToken = localStorage.getItem('refreshToken');
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  const getInitialData = async () => {
    try {
      if (!refreshToken) {
        navigator(ApplicationRoute.Login);

        return;
      }

      const userAccount = await getAccount();

      if (!userAccount) {
        navigator(ApplicationRoute.Login);

        return;
      }

      await permissionsStore.getAllPermissions();

      const companyProfileResult = await getCompanyProfile();

      await getMdaContacts();
      setAuthenticated();

      if (userAccount!.accountStatus === AccountStatus.Pending) {
        navigator(ApplicationRoute.ConfirmEmail);

        return;
      }

      const hasAllRequiredPermissions = hasPermission(userAccount!.permissions, requiredPermissions);

      if (location.pathname !== ApplicationRoute.MdaPreview) {
        if (!companyProfileResult.masterDealerAgreementWasSigned) {
          navigator(ApplicationRoute.AccountStatus);

          return;
        } else {
          const { totalApplications } = await applicationsStore.getApplications();

          if (
            totalApplications === 0 ||
            dayjs().diff(companyProfileResult.masterDealerAgreementDateSigned, 'day') < MDA_WAS_SIGNED_DAYS_PASSED
          ) {
            navigator(ApplicationRoute.AccountStatus);

            return;
          }
        }
      }

      if (!hasAllRequiredPermissions) {
        if (requiredPermissions.includes(PermissionName.ConsumerApplication)) {
          // some users don't have permissions for the default Home page with applications
          // in this case they should be redirected to the Account page if mda was submitted
          navigator(ApplicationRoute.MyProfile);

          return;
        }

        navigator(ApplicationRoute.Other);

        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();

    // we need login User only once when page is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAllRequiredPermissions = account?.permissions && hasPermission(account?.permissions, requiredPermissions);
  const shouldOpenFeatureRestricted =
    authStore.account?.accountStatus === AccountStatus.Active &&
    !hasAllRequiredPermissions &&
    !companyProfile?.masterDealerAgreementWasSigned;

  useEffect(() => {
    if (shouldOpenFeatureRestricted) {
      openFeatureRestricted();
    }
  }, [shouldOpenFeatureRestricted, openFeatureRestricted]);

  if (isLoading) {
    return <Spinner />;
  }

  if (shouldOpenFeatureRestricted) {
    return <></>;
  }

  return (
    <>
      <Outlet />
      <ConsentDialog />
    </>
  );
};

export default observer(PrivateRoute);
